import { Formik } from 'formik';
import { PhoneType } from '../../../../API/Enums';
import { CTAButton } from '../../../../controls/ButtonBase';
import { GMFormCheckbox } from '../../../../controls/Checkbox';
import { GMDialog } from '../../../../controls/Dialog';
import { GMForm } from '../../../../controls/Form';
import { GMGrid } from '../../../../controls/Grid';
import { GMFormSelect, GMMenuItem } from '../../../../controls/Select';
import { GMFormTextField, GMFormNumericField } from '../../../../controls/TextField';
import { nameof } from '../../../../helpers/nameof';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { DevQRDealerType, DevQRType, ExtendedDevQRCodeData, ScanDevQRCodeData, ScanQRCodeDevToolsProps } from './ScanQRCodeDevTools.types';
import { JSXCase, JSXSwitch } from '../../../../controls/JSXSwitch';
import css from './ScanQRCodeDevTools.module.scss';
import * as Yup from 'yup';
import { useLocalizationExtension } from '../../../../context/Localization/localization';
import clone from 'just-clone';

/**
 * Displays a dialog of DEV only fields that simulate the result of scanning a QR code.
 *
 * @param onScan - {@link ScanQRCodeDevToolsProps.onScan onScan} description.
 * @param isDevToolsOpen - {@link ScanQRCodeDevToolsProps.isDevToolsOpen isDevToolsOpen} description.
 * @param setIsDevToolsOpen - {@link ScanQRCodeDevToolsProps.setIsDevToolsOpen setIsDevToolsOpen} description.
 * @param qrCodeType - {@link ScanQRCodeDevToolsProps.qrCodeType qrCodeType} description. (default: 'pickup')
 */
export const ScanQRCodeDevTools: GoMotoComponent<ScanQRCodeDevToolsProps> = ({
    onScan,
    isDevToolsOpen,
    setIsDevToolsOpen,
    qrCodeType = 'pickup',
}) => {
    const { formatRequired } = useLocalizationExtension();
    const initialValues: ScanDevQRCodeData = {
        dmsOrNonDMS: DevQRType.NonDMS,
        dmsOrDealer: DevQRDealerType.Dealer,
        key: '',
        apptNo: '',
        dealerKey: '',
        dmsDealerKey: '',
        dmsStoreId: '',
        dmsAreaId: '',
        currentUrl: '',
        mobileCheckInComplete: false,
        maskPhone: false,
        maskEmail: false,
        maskVin: false,
        phoneType: PhoneType.home,
        total: 0,
        paid: 'no payment due',
        mfaCode: '',
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={
                qrCodeType !== 'mfa' ? (
                    Yup.object()
                        .shape({
                            [nameof<ScanDevQRCodeData>('key')]: Yup.string()
                                .when(nameof<ScanDevQRCodeData>('apptNo'), {
                                    is: (value: ScanDevQRCodeData['apptNo']) => {
                                        return !value;
                                    },
                                    then: (schema) => schema.required(formatRequired('Key')),
                                }),
                            [nameof<ScanDevQRCodeData>('apptNo')]: Yup.string()
                                .when(nameof<ScanDevQRCodeData>('key'), {
                                    is: (value: ScanDevQRCodeData['key']) => {
                                        return !value;
                                    },
                                    then: (schema) => schema.required(formatRequired('Appointment Number')),
                                }),
                        }, [
                            [nameof<ScanDevQRCodeData>('key'), nameof<ScanDevQRCodeData>('apptNo')],
                        ])
                        .test(
                            'at-least-one',
                            formatRequired('Either Appointment Key or Number'),
                            (values) => values.key?.trim() !== '' || values.apptNo?.trim() !== '',
                        )
                ) : (
                    Yup.object()
                        .shape({
                            [nameof<ScanDevQRCodeData>('mfaCode')]: Yup.string()
                                .required(formatRequired('Plaintext QR Code')),
                        })
                )
            }
            onSubmit={(values, { setFieldError }) => {
                const { mfaCode, ...restValues } = values;

                if (qrCodeType !== 'mfa') {
                    const newValues: Omit<ExtendedDevQRCodeData, 'mfaCode'> = clone(restValues);

                    if (newValues.key && newValues.apptNo) {
                        setFieldError(nameof<ScanDevQRCodeData>('key'), 'Appointment Key and ApptNo cannot be used at the same time');
                        setFieldError(nameof<ScanDevQRCodeData>('apptNo'), 'Appointment Key and ApptNo cannot be used at the same time');
                    }
                    else {
                        onScan(newValues);
                    }
                }
                else {
                    onScan({ mfaCode });
                }
            }}
        >
            {({ values, submitForm, setFieldValue }) => (
                <GMDialog
                    open={isDevToolsOpen}
                    ariaLabel={'Dev Tools Dialog'}
                    title={'Dev Tools'}
                    className={css.dialogTitle}
                    paperDialogClasses={css.dialog}
                    actions={[
                        <CTAButton
                            onClick={() => setIsDevToolsOpen(false)}
                            label={'Cancel'}
                            styleType={'secondary'}
                            id={'Cancel'}
                            ariaLabel={'Cancel'}
                            hideIcon
                        />,
                        <CTAButton
                            onClick={submitForm}
                            label={'Scan'}
                            styleType={'primary'}
                            id={'Submit'}
                            ariaLabel={'Submit'}
                        />,
                    ]}
                    onClose={() => setIsDevToolsOpen(false)}
                >
                    <GMForm
                        topDivider
                        hideKeyboard={true}
                    >
                        <JSXSwitch>
                            <JSXCase condition={qrCodeType === 'checkin'}>
                                <GMGrid
                                    container
                                    className={css.gridRows}
                                    xs={12}
                                >
                                    <GMGrid
                                        container
                                        xs={12}
                                        columnGap={2}
                                        wrap={'nowrap'}
                                    >
                                        <GMGrid xs={4}>
                                            <GMFormSelect
                                                id={nameof<ScanDevQRCodeData>('dmsOrNonDMS')}
                                                name={nameof<ScanDevQRCodeData>('dmsOrNonDMS')}
                                                label={'QR Type'}
                                            >
                                                <GMMenuItem value={DevQRType.NonDMS}>
                                                    {'Non-DMS\r'}
                                                </GMMenuItem>
                                                <GMMenuItem value={DevQRType.DMS}>
                                                    {'DMS\r'}
                                                </GMMenuItem>
                                            </GMFormSelect>
                                        </GMGrid>
                                        <GMGrid xs={8}>
                                            {values.dmsOrNonDMS === DevQRType.NonDMS ? (
                                                <GMFormTextField
                                                    name={nameof<ScanDevQRCodeData>('key')}
                                                    id={nameof<ScanDevQRCodeData>('key')}
                                                    title={'Appointment Key'}
                                                    label={'Appointment Key'}
                                                    ariaLabel={'Appointment Key'}
                                                    required={!values.apptNo}
                                                />
                                            ) : (
                                                <GMFormTextField
                                                    name={nameof<ScanDevQRCodeData>('apptNo')}
                                                    id={nameof<ScanDevQRCodeData>('apptNo')}
                                                    title={'Appointment Number'}
                                                    label={'Appointment Number'}
                                                    ariaLabel={'Appointment Number'}
                                                    required={!values.key}
                                                />
                                            )}
                                        </GMGrid>
                                    </GMGrid>
                                    <GMGrid container xs={12}>
                                        {values.dmsOrNonDMS === DevQRType.NonDMS || (values.dmsOrNonDMS === DevQRType.DMS && values.dmsOrDealer === DevQRDealerType.Dealer) ? (
                                            <GMGrid
                                                container
                                                xs={12}
                                                columnGap={2}
                                                wrap={'nowrap'}
                                            >
                                                {values.dmsOrNonDMS !== DevQRType.NonDMS ? (
                                                    <GMGrid xs={5}>
                                                        <GMFormSelect
                                                            id={nameof<ScanDevQRCodeData>('dmsOrDealer')}
                                                            name={nameof<ScanDevQRCodeData>('dmsOrDealer')}
                                                            label={'QR Dealer Type'}
                                                        >
                                                            <GMMenuItem value={DevQRDealerType.Dealer}>
                                                                {'Dealer Key\r'}
                                                            </GMMenuItem>
                                                            <GMMenuItem value={DevQRDealerType.DMSDealer}>
                                                                {'DMS Dealer Key\r'}
                                                            </GMMenuItem>
                                                        </GMFormSelect>
                                                    </GMGrid>
                                                ) : null}
                                                <GMGrid xs={values.dmsOrNonDMS !== DevQRType.NonDMS ? 7 : 12}>
                                                    <GMFormTextField
                                                        name={nameof<ScanDevQRCodeData>('dealerKey')}
                                                        id={nameof<ScanDevQRCodeData>('dealerKey')}
                                                        title={'Dealer Key'}
                                                        label={'Dealer Key'}
                                                        ariaLabel={'Dealer Key'}
                                                    />
                                                </GMGrid>
                                            </GMGrid>
                                        ) : (
                                            <>
                                                <GMGrid
                                                    container
                                                    xs={12}
                                                    columnGap={2}
                                                    wrap={'nowrap'}
                                                >
                                                    <GMGrid xs={5}>
                                                        <GMFormSelect
                                                            id={nameof<ScanDevQRCodeData>('dmsOrDealer')}
                                                            name={nameof<ScanDevQRCodeData>('dmsOrDealer')}
                                                            label={'QR Dealer Type'}
                                                        >
                                                            <GMMenuItem value={DevQRDealerType.Dealer}>
                                                                {'Dealer Key\r'}
                                                            </GMMenuItem>
                                                            <GMMenuItem value={DevQRDealerType.DMSDealer}>
                                                                {'DMS Dealer Key\r'}
                                                            </GMMenuItem>
                                                        </GMFormSelect>
                                                    </GMGrid>
                                                    <GMGrid xs={7}>
                                                        <GMFormTextField
                                                            name={nameof<ScanDevQRCodeData>('dmsDealerKey')}
                                                            id={nameof<ScanDevQRCodeData>('dmsDealerKey')}
                                                            title={'DMS Dealer Key'}
                                                            label={'DMS Dealer Key'}
                                                            ariaLabel={'DMS Dealer Key'}
                                                        />
                                                    </GMGrid>
                                                </GMGrid>
                                                <GMGrid
                                                    container
                                                    xs={12}
                                                    columnGap={2}
                                                    wrap={'nowrap'}
                                                >
                                                    <GMGrid xs={6}>
                                                        <GMFormTextField
                                                            name={nameof<ScanDevQRCodeData>('dmsStoreId')}
                                                            id={nameof<ScanDevQRCodeData>('dmsStoreId')}
                                                            title={'DMS Store ID'}
                                                            label={'DMS Store ID'}
                                                            ariaLabel={'DMS Store ID'}
                                                        />
                                                    </GMGrid>
                                                    <GMGrid xs={6}>
                                                        <GMFormTextField
                                                            name={nameof<ScanDevQRCodeData>('dmsAreaId')}
                                                            id={nameof<ScanDevQRCodeData>('dmsAreaId')}
                                                            title={'DMS Area ID'}
                                                            label={'DMS Area ID'}
                                                            ariaLabel={'DMS Area ID'}
                                                        />
                                                    </GMGrid>
                                                </GMGrid>
                                            </>
                                        )}
                                    </GMGrid>
                                    <GMGrid container xs={12}>
                                        <GMGrid
                                            xs={4}
                                            padding={1}
                                            marginBottom={'1vmin'}
                                        >
                                            <GMFormCheckbox
                                                label={'Mask Phone'}
                                                labelVariant={'heading'}
                                                labelPlacement={'top'}
                                                checked={values.maskPhone}
                                                onChange={() => {
                                                    setFieldValue(nameof<ScanDevQRCodeData>('maskPhone'), !values.maskPhone);
                                                }}
                                            />
                                        </GMGrid>
                                        <GMGrid
                                            xs={4}
                                            padding={1}
                                            marginBottom={'1vmin'}
                                        >
                                            <GMFormCheckbox
                                                label={'Mask Email'}
                                                labelVariant={'heading'}
                                                labelPlacement={'top'}
                                                checked={values.maskEmail}
                                                onChange={() => {
                                                    setFieldValue(nameof<ScanDevQRCodeData>('maskEmail'), !values.maskEmail);
                                                }}
                                            />
                                        </GMGrid>
                                        <GMGrid
                                            xs={4}
                                            padding={1}
                                            marginBottom={'1vmin'}
                                        >
                                            <GMFormCheckbox
                                                label={'Mask Vin'}
                                                labelVariant={'heading'}
                                                labelPlacement={'top'}
                                                checked={values.maskVin}
                                                onChange={() => {
                                                    setFieldValue(nameof<ScanDevQRCodeData>('maskVin'), !values.maskVin);
                                                }}
                                            />
                                        </GMGrid>
                                    </GMGrid>
                                    <GMGrid container xs={12}>
                                        <GMGrid
                                            xs={12}
                                            padding={1}
                                            marginBottom={'1vmin'}
                                        >
                                            <GMFormCheckbox
                                                label={'Mobile Check-In Complete'}
                                                labelVariant={'heading'}
                                                labelPlacement={'top'}
                                                checked={values.mobileCheckInComplete}
                                                onChange={() => {
                                                    setFieldValue(nameof<ScanDevQRCodeData>('mobileCheckInComplete'), !values.mobileCheckInComplete);
                                                }}
                                            />
                                        </GMGrid>
                                    </GMGrid>
                                    <GMGrid xs={12}>
                                        <GMFormSelect
                                            id={'Phone Type'}
                                            name={nameof<ScanDevQRCodeData>('phoneType')}
                                            label={'Phone Type'}
                                        >
                                            <GMMenuItem key={'Phone Type 0'} value={0}>{'Home'}</GMMenuItem>
                                            <GMMenuItem key={'Phone Type 1'} value={1}>{'Cell'}</GMMenuItem>
                                            <GMMenuItem key={'Phone Type 2'} value={2}>{'Work'}</GMMenuItem>
                                            <GMMenuItem key={'Phone Type 3'} value={3}>{'Other'}</GMMenuItem>
                                        </GMFormSelect>
                                    </GMGrid>
                                    <GMGrid
                                        container
                                        xs={12}
                                        columnGap={2}
                                        wrap={'nowrap'}
                                    >
                                        <GMGrid xs={6}>
                                            <GMFormNumericField
                                                name={nameof<ScanDevQRCodeData>('total')}
                                                id={nameof<ScanDevQRCodeData>('total')}
                                                title={'Payment Total'}
                                                label={'Payment Total'}
                                                ariaLabel={'Payment Total'}
                                            />
                                        </GMGrid>
                                        <GMGrid xs={6}>
                                            <GMFormSelect
                                                id={'Payment Status'}
                                                name={nameof<ScanDevQRCodeData>('paid')}
                                                label={'Payment Status'}
                                            >
                                                <GMMenuItem key={'Payment Status 0'} value={'no payment due'}>{'No Payment Due'}</GMMenuItem>
                                                <GMMenuItem key={'Payment Status 1'} value={'paid'}>{'Paid'}</GMMenuItem>
                                                <GMMenuItem key={'Payment Status 2'} value={'not paid'}>{'Not Paid'}</GMMenuItem>
                                                <GMMenuItem key={'Payment Status 3'} value={'marked paid'}>{'Marked Paid'}</GMMenuItem>
                                            </GMFormSelect>
                                        </GMGrid>
                                    </GMGrid>
                                </GMGrid>
                            </JSXCase>
                            <JSXCase condition={qrCodeType === 'pickup'}>
                                <GMGrid
                                    container
                                    className={css.gridRows}
                                    xs={12}
                                >
                                    <GMGrid xs={12}>
                                        <GMFormTextField
                                            name={nameof<ScanDevQRCodeData>('key')}
                                            id={nameof<ScanDevQRCodeData>('key')}
                                            title={'Appointment Key'}
                                            label={'Appointment Key'}
                                            ariaLabel={'Appointment Key'}
                                            required={!values.apptNo}
                                        />
                                    </GMGrid>
                                    <GMGrid
                                        container
                                        xs={12}
                                        columnGap={2}
                                        wrap={'nowrap'}
                                    >
                                        <GMGrid xs={6}>
                                            <GMFormNumericField
                                                name={nameof<ScanDevQRCodeData>('total')}
                                                id={nameof<ScanDevQRCodeData>('total')}
                                                title={'Payment Total'}
                                                label={'Payment Total'}
                                                ariaLabel={'Payment Total'}
                                            />
                                        </GMGrid>
                                        <GMGrid xs={6}>
                                            <GMFormSelect
                                                id={'Payment Status'}
                                                name={nameof<ScanDevQRCodeData>('paid')}
                                                label={'Payment Status'}
                                            >
                                                <GMMenuItem key={'Payment Status 0'} value={'no payment due'}>{'No Payment Due'}</GMMenuItem>
                                                <GMMenuItem key={'Payment Status 1'} value={'paid'}>{'Paid'}</GMMenuItem>
                                                <GMMenuItem key={'Payment Status 2'} value={'not paid'}>{'Not Paid'}</GMMenuItem>
                                                <GMMenuItem key={'Payment Status 3'} value={'marked paid'}>{'Marked Paid'}</GMMenuItem>
                                            </GMFormSelect>
                                        </GMGrid>
                                    </GMGrid>
                                </GMGrid>
                            </JSXCase>
                            <JSXCase condition={qrCodeType === 'mfa'}>
                                <GMGrid
                                    container
                                    className={css.gridRows}
                                    xs={12}
                                >
                                    <GMGrid xs={12}>
                                        <GMFormTextField
                                            name={nameof<ScanDevQRCodeData>('mfaCode')}
                                            id={nameof<ScanDevQRCodeData>('mfaCode')}
                                            title={'Plaintext QR Code'}
                                            label={'Plaintext QR Code'}
                                            ariaLabel={'Plaintext QR Code'}
                                            required={qrCodeType === 'mfa'}
                                        />
                                    </GMGrid>
                                </GMGrid>
                            </JSXCase>
                        </JSXSwitch>
                    </GMForm>
                </GMDialog>
            )}
        </Formik>
    );
};